import { builder } from "@builder.io/react";
import { BuilderContent } from "@builder.io/sdk";
import { UserAttributes } from "@builder.io/sdk/dist/src/builder.class";
// the following line is needed to load the builder.io widgets (carousel, tabs, etc.)
// eslint-disable-next-line import/no-unassigned-import
import "@builder.io/widgets/dist/lib/builder-widgets-async";
import { registerBuilderComponents } from "@faire/web/builder/__generated__/registerBuilderComponents";
import { logError } from "@faire/web/common/logging";
import { TimingMarker } from "@faire/web/common/timings/TimingMarker";
import { trackRetailerMarketingBuilderLandingPageStatus } from "@faire/web-api/events/retailerMarketing/actionUnknown/builderLandingPageStatus";
import once from "lodash/once";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  getBuilderAPIKey,
  getLocaleId,
  getSessionCountry,
} from "./builderUtils";

const initializeBuilder = once((apiKey: string) => {
  builder.init(apiKey);
  registerBuilderComponents();
});

export const useBuilderSection = (
  modelName: string,
  enabled: boolean, // prevent over-calling builder API
  targets?: UserAttributes
): BuilderContent | undefined => {
  const { pathname } = useLocation();
  const userCountry = getSessionCountry();
  const apiKey = getBuilderAPIKey();

  // e.g. "en-US", "fr-FR", "de-DE"
  // if not set, use "Default" as the default locale
  const locale = getLocaleId()?.replace("_", "-") ?? "Default";

  const [builderSectionContent, setBuilderSectionContent] = useState<
    BuilderContent | undefined
  >(undefined);

  useEffect(() => {
    const defaultUserAttributes: UserAttributes = {
      locale,
      urlPath: pathname,
      userCountry,
    };

    if (apiKey && enabled) {
      const start = new TimingMarker();
      initializeBuilder(apiKey);
      builder
        .get(modelName, {
          userAttributes: {
            ...defaultUserAttributes,
            ...targets,
          },
          options: {
            locale,
          },
        })
        .promise()
        .then((content) => {
          trackRetailerMarketingBuilderLandingPageStatus(modelName, "success", {
            start,
            end: new TimingMarker(),
          });
          setBuilderSectionContent(content);
        })
        .catch((_error) => {
          logError("fetching Builder content error" + _error);
          trackRetailerMarketingBuilderLandingPageStatus(modelName, "error", {
            start,
            end: new TimingMarker(),
          });
        });
    }
  }, [modelName, apiKey, enabled, pathname, userCountry, locale, targets]);

  return builderSectionContent;
};
